import React, { useEffect, useRef } from "react";
import { MdOutlineInfo } from "react-icons/md";
import "./SharedStyles.scss";

interface IProps {
  message: string;
}

const hasOwnStyles = (html: string): boolean =>
  /<style[\s>]/i.test(html) || /<link[^>]+stylesheet/i.test(html) || /<!DOCTYPE/i.test(html);

const isHtmlMessage = (content: string): boolean => {
  const text = (content ?? "").trim();
  return hasOwnStyles(text) || /<[a-z][^>]*>/i.test(text);
};
const FILE_LINK_PATTERN =
  /\.(pdf|docx?|xlsx?|pptx?|csv|zip|rar|7z|tar|gz|jpg|jpeg|png|gif|svg|webp|mp4|mp3|wav|ogg)(\?|#|$)/i;

const applyLinkSecurity = (root: ParentNode) => {
  root.querySelectorAll("a[href]").forEach((link) => {
    const anchor = link as HTMLAnchorElement;
    const href = anchor.getAttribute("href") || "";

    const isFileLink = FILE_LINK_PATTERN.test(anchor.pathname || href);

    const isInternalSpaLink =
      !isFileLink &&
      (href.startsWith("/") ||
        href.startsWith("#") ||
        href.startsWith("?") ||
        (anchor.hostname && anchor.hostname === window.location.hostname));

    if (isInternalSpaLink) {
      anchor.removeAttribute("target");
      anchor.removeAttribute("rel");
    } else {
      anchor.setAttribute("target", "_blank");
      anchor.setAttribute("rel", "noopener noreferrer");
    }
  });
};

const EAPageMessage: React.FC<IProps> = ({ message }) => {
  const safeMessage = (message ?? "").trim();
  const shadowHostRef = useRef<HTMLDivElement>(null);
  const articleRef = useRef<HTMLElement>(null);
  const isIsolated = hasOwnStyles(safeMessage);
  const isHtml = isHtmlMessage(safeMessage);

  useEffect(() => {
    if (!isHtml) return;

    if (isIsolated) {
      const host = shadowHostRef.current;
      if (!host) return;

      const shadow = host.shadowRoot ?? host.attachShadow({ mode: "open" });
      shadow.innerHTML = safeMessage;
      applyLinkSecurity(shadow);
    } else {
      if (!articleRef.current) return;
      applyLinkSecurity(articleRef.current);
    }
  }, [safeMessage, isIsolated, isHtml]);

  if (!safeMessage || safeMessage.length <= 12) return null;

  if (isHtml) {
    return (
      <div className="page-message-content">
        {isIsolated ? (
          <div ref={shadowHostRef} />
        ) : (
          <article ref={articleRef} dangerouslySetInnerHTML={{ __html: safeMessage }} />
        )}
      </div>
    );
  }

  return (
    <div className="page-message">
      <MdOutlineInfo className="page-message-icon" />
      <article>{safeMessage}</article>
    </div>
  );
};

export default EAPageMessage;
